import { Component, OnInit, ErrorHandler } from '@angular/core';
import { UserService } from '../shared/user.service';
import swal from 'sweetalert2';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { Router, ActivatedRoute } from '@angular/router';

interface ReportColumn {
  field: string;
  label: string;
  visible: boolean;
}

@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.css']
})
export class ReportsComponent implements OnInit {

  showReport: boolean = false;
  selectedReport: string = '';
  reportTitle: string = '';
  reportDescription: string = '';

  // ============================================================
  // ACTIVE FILTER DATA (Used by the viewer)
  // ============================================================
  filteredReportData: any[] = [];
  filterType: string = ''; 
  filterFromDate: string = '';
  filterToDate: string = '';
  filterDate: string = '';
  filterMonth: string = '';
  filterYear: string = '';
  filterField: string = 'FinalNotificationDate'; 

  // ============================================================
  // REPORT TYPES
  // ============================================================
  readonly LAND_ACQUISITION = 'LAND_ACQUISITION';
  readonly KHATIDARS = 'KHATIDARS';
  readonly SURVEY = 'SURVEY';
  readonly NOTICE_12_2 = 'NOTICE_12_2';
  readonly NOTICE_16_2 = 'NOTICE_16_2';

  // ============================================================
  // INDIVIDUAL REPORT FILTERS (Keeps states isolated on the list page)
  // ============================================================
  individualFilters: any = {
    LAND_ACQUISITION: { field: 'FinalNotificationDate', type: '', fromDate: '', toDate: '', date: '', month: '', year: '' },
    KHATIDARS: { field: 'FinalNotificationDate', type: '', fromDate: '', toDate: '', date: '', month: '', year: '' },
    SURVEY: { field: 'FinalNotificationDate', type: '', fromDate: '', toDate: '', date: '', month: '', year: '' },
    NOTICE_12_2: { field: 'NoticeDate', type: '', fromDate: '', toDate: '', date: '', month: '', year: '' },
    NOTICE_16_2: { field: 'NoticeDate', type: '', fromDate: '', toDate: '', date: '', month: '', year: '' }
  };

  // ============================================================
  // LAYOUT / VILLAGE
  // ============================================================
  layouts: any[] = [];
  villages: any[] = [];
  selectedLayoutId: number = 0;
  selectedVillageId: number = 0;

  reportData: any[] = [];
  loading: boolean = false;
  errorMessage: string = '';
  noDataMessage: string = '';

  constructor(
    private userService: UserService,
    private router: Router,
    private route: ActivatedRoute,
    private errorHandler: ErrorHandler
  ) {}

  ngOnInit(): void {
    this.loadLayouts();
  }

  loadLayouts(): void {
    this.loading = true;
    this.errorMessage = '';
    this.userService.Getalllayouts().subscribe(
      (data: any) => {
        this.loading = false;
        this.layouts = this.getArrayFromResponse(data);
      },
      error => {
        this.loading = false;
        this.layouts = [];
        this.errorMessage = 'Unable to load layouts.';
      }
    );
  }

  hasSelection(): boolean {
    return !!this.selectedLayoutId && !!this.selectedVillageId;
  }

  // ============================================================
  // SET ACTIVE FILTERS (Copies the individual row's filter to the active state)
  // ============================================================
  setActiveFilters(reportKey: string): void {
    const f = this.individualFilters[reportKey];
    if (reportKey === this.NOTICE_12_2 || reportKey === this.NOTICE_16_2) {
      this.filterField = 'NoticeDate';
    } else {
      this.filterField = f.field || 'FinalNotificationDate';
    }
    this.filterType = f.type;
    this.filterFromDate = f.fromDate || f.date || '';
    this.filterToDate = f.toDate || '';
    this.filterDate = f.date || f.fromDate || '';
    this.filterMonth = f.month;
    this.filterYear = f.year;
  }

  // ============================================================
  // LOAD REPORT METHODS
  // ============================================================

  openLandAcquisitionReport(): void {
    this.selectedReport = this.LAND_ACQUISITION;
    this.reportTitle = 'Layout Details Report';
    this.reportDescription = 'View detailed information related to land acquisition, layouts, villages and acquisition records.';
    this.showReport = true;
    this.resetReportData();
    this.setActiveFilters(this.LAND_ACQUISITION);
    this.loadLandAcquisitionReport();
  }

  loadLandAcquisitionReport(): void {
    this.loading = true;
    this.errorMessage = '';
    this.noDataMessage = '';

    this.userService.Getalllayouts().subscribe(
      (data: any) => {
        this.loading = false;
        this.reportData = this.getArrayFromResponse(data);
        this.applyFilters(); 

        if (this.filteredReportData.length === 0) {
          this.noDataMessage = 'No layout records found matching the criteria.';
        }
      },
      error => {
        this.loading = false;
        this.reportData = [];
        this.errorMessage = 'Unable to load Layout Details Report.';
      }
    );
  }

  openKhatedarReport(): void {
    if (!this.hasSelection()) return;
    this.selectedReport = this.KHATIDARS;
    this.reportTitle = 'Khathedars Report';
    this.reportDescription = 'View khathedar and land owner details associated with the selected layout and village.';
    this.showReport = true;
    this.resetReportData();
    this.setActiveFilters(this.KHATIDARS);
    this.loadKhatedarsReport();
  }

  loadKhatedarsReport(): void {
    this.loading = true;
    this.errorMessage = '';
    this.noDataMessage = '';

    this.userService.GetAllKhatedarbyLayoutAndVillage(this.selectedLayoutId, this.selectedVillageId).subscribe(
      (data: any) => {
        this.loading = false;
        this.reportData = this.getArrayFromResponse(data);
        this.applyFilters(); 

        if (this.filteredReportData.length === 0) {
          this.noDataMessage = 'No khathedar records found matching the criteria.';
        }
      },
      error => {
        this.loading = false;
        this.reportData = [];
        this.errorMessage = 'Unable to load Khathedars Report.';
      }
    );
  }

  openSurveyReport(): void {
    if (!this.hasSelection()) return;
    this.selectedReport = this.SURVEY;
    this.reportTitle = 'Survey No. Report';
    this.reportDescription = 'View land acquisition information based on survey numbers and associated land records.';
    this.showReport = true;
    this.resetReportData();
    this.setActiveFilters(this.SURVEY);
    this.loadSurveyReport();
  }

  loadSurveyReport(): void {
    this.loading = true;
    this.errorMessage = '';
    this.noDataMessage = '';

    this.userService.GetAllSurvrynobyLayoutAndVillage(this.selectedLayoutId, this.selectedVillageId).subscribe(
      (data: any) => {
        this.loading = false;
        this.reportData = this.getArrayFromResponse(data);
        this.applyFilters(); 

        if (this.filteredReportData.length === 0) {
          this.noDataMessage = 'No Survey records found matching the criteria.';
        }
      },
      error => {
        this.loading = false;
        this.reportData = [];
        this.errorMessage = 'Unable to load Survey No. Report.';
      }
    );
  }

  openNotice12_2Report(): void {
    if (!this.hasSelection()) return;
    this.selectedReport = this.NOTICE_12_2;
    this.reportTitle = 'Notice 12(2) Report';
    this.reportDescription = 'View details of notices issued under Section 12(2) for the selected Layout and Village.';
    this.showReport = true;
    this.resetReportData();
    this.setActiveFilters(this.NOTICE_12_2);
    this.loadNotice12_2Report();
  }

  loadNotice12_2Report(): void {
    this.loading = true;
    this.reportData = [];
    this.errorMessage = '';
    this.noDataMessage = '';

    this.userService.GetAll12_2(this.selectedLayoutId, this.selectedVillageId).subscribe(
      (data: any) => {
        this.loading = false;
        this.reportData = this.getArrayFromResponse(data);
        this.applyFilters(); 

        if (this.filteredReportData.length === 0) {
          this.noDataMessage = 'No Notice 12(2) records found matching the criteria.';
        }
      },
      error => {
        this.loading = false;
        this.reportData = [];
        this.errorMessage = 'Unable to load Notice 12(2) Report.';
      }
    );
  }

  openNotice16_2Report(): void {
    if (!this.hasSelection()) return;
    this.selectedReport = this.NOTICE_16_2;
    this.reportTitle = 'Notice 16(2) Report';
    this.reportDescription = 'View details of notices issued under Section 16(2) for the selected Layout and Village.';
    this.showReport = true;
    this.resetReportData();
    this.setActiveFilters(this.NOTICE_16_2);
    this.loadNotice16_2Report();
  }

  loadNotice16_2Report(): void {
    this.loading = true;
    this.reportData = [];
    this.errorMessage = '';
    this.noDataMessage = '';

    this.userService.GetAll16_2(this.selectedLayoutId, this.selectedVillageId).subscribe(
      (data: any) => {
        this.loading = false;
        this.reportData = this.getArrayFromResponse(data);
        this.applyFilters();

        if (this.filteredReportData.length === 0) {
          this.noDataMessage = 'No Notice 16(2) records found matching the criteria.';
        }
      },
      error => {
        this.loading = false;
        this.reportData = [];
        this.errorMessage = 'Unable to load Notice 16(2) Report.';
      }
    );
  }

  resetReportData(): void {
    this.reportData = [];
    this.filteredReportData = [];
    this.errorMessage = '';
    this.noDataMessage = '';
  }

  clearIndividualFilter(reportKey: string): void {
    if (!this.individualFilters[reportKey]) return;
    this.individualFilters[reportKey].type = '';
    this.individualFilters[reportKey].fromDate = '';
    this.individualFilters[reportKey].toDate = '';
    this.individualFilters[reportKey].date = '';
    this.individualFilters[reportKey].month = '';
    this.individualFilters[reportKey].year = '';
    if (reportKey === this.NOTICE_12_2 || reportKey === this.NOTICE_16_2) {
      this.individualFilters[reportKey].field = 'NoticeDate';
    } else {
      this.individualFilters[reportKey].field = 'FinalNotificationDate';
    }
  }

  onLayoutChange(): void {
    this.selectedVillageId = 0;
    this.villages = [];
    this.errorMessage = '';

    if (!this.selectedLayoutId) return;

    this.loading = true;
    this.userService.GetAllvillagesbylayoutid(this.selectedLayoutId).subscribe(
      (data: any) => {
        this.loading = false;
        this.villages = this.getArrayFromResponse(data);
      },
      error => {
        this.loading = false;
        this.villages = [];
        this.errorMessage = 'Unable to load villages.';
      }
    );
  }

  onVillageChange(): void {
    this.errorMessage = '';
  }

  // ============================================================
  // DATE PARSER
  // ============================================================
  parseDateParts(dateVal: any): { day: number; month: number; year: number } | null {
    if (!dateVal) return null;

    if (dateVal instanceof Date && !isNaN(dateVal.getTime())) {
      return {
        day: dateVal.getDate(),
        month: dateVal.getMonth() + 1,
        year: dateVal.getFullYear()
      };
    }

    if (typeof dateVal !== 'string') {
      dateVal = String(dateVal);
    }

    dateVal = dateVal.trim();
    if (!dateVal) return null;

    const cleanDateStr = dateVal.split(' ')[0].split('T')[0];

    const dmyMatch = cleanDateStr.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
    if (dmyMatch) {
      return {
        day: parseInt(dmyMatch[1], 10),
        month: parseInt(dmyMatch[2], 10),
        year: parseInt(dmyMatch[3], 10)
      };
    }

    const ymdMatch = cleanDateStr.match(/^(\d{4})[./-](\d{1,2})[./-](\d{1,2})$/);
    if (ymdMatch) {
      return {
        year: parseInt(ymdMatch[1], 10),
        month: parseInt(ymdMatch[2], 10),
        day: parseInt(ymdMatch[3], 10)
      };
    }

    const parsed = new Date(dateVal);
    if (!isNaN(parsed.getTime())) {
      return {
        day: parsed.getDate(),
        month: parsed.getMonth() + 1,
        year: parsed.getFullYear()
      };
    }

    return null;
  }

  // ============================================================
  // APPLY FILTERS 
  // ============================================================
  applyFilters(): void {
    if (!this.filterType) {
      this.filteredReportData = [...this.reportData];
      this.noDataMessage = this.filteredReportData.length === 0 ? 'No records found.' : '';
      return;
    }

    this.filteredReportData = this.reportData.filter(item => {
      let dateStringVal = '';

      if (this.filterField === 'NoticeDate' || this.selectedReport === this.NOTICE_12_2 || this.selectedReport === this.NOTICE_16_2) {
        if (this.selectedReport === this.NOTICE_12_2) {
          dateStringVal = this.getValue(item, ['notice12_2Date', 'Notice12_2Date', 'noticeDate', 'NoticeDate', 'date', 'Date']);
        } else if (this.selectedReport === this.NOTICE_16_2) {
          dateStringVal = this.getValue(item, ['notice16_2Date', 'Notice16_2Date', 'noticeDate', 'NoticeDate', 'date', 'Date']);
        } else {
          dateStringVal = this.getValue(item, ['NoticeDate', 'noticeDate', 'notice12_2Date', 'Notice12_2Date', 'notice16_2Date', 'Notice16_2Date', 'date', 'Date']);
        }
      } else if (this.filterField === 'PreliminaryNotificationDate') {
        // Look up Preliminary Notification
        dateStringVal = this.getValue(item, [
          'PreliminaryNotificationDate',
          'preliminaryNotificationDate',
          'PreliminaryNotificationdate',
          'Preliminary_Notification_Date',
          'preliminary_notification_date'
        ]);

        // Fallback to Layout's preliminary if child misses it
        if (!dateStringVal && this.selectedLayoutId) {
          const selectedLayout = this.layouts.find(x => this.getValue(x, ['layoutId', 'LayoutId', 'id', 'Id']) == this.selectedLayoutId);
          if (selectedLayout) {
            dateStringVal = this.getValue(selectedLayout, [
              'PreliminaryNotificationDate',
              'preliminaryNotificationDate',
              'PreliminaryNotificationdate',
              'Preliminary_Notification_Date',
              'preliminary_notification_date'
            ]);
          }
        }
      } else {
        // Fallback to Final Notification
        dateStringVal = this.getValue(item, [
          'FinalNotificationDate',
          'finalNotificationDate',
          'FinalNotificationdate',
          'Final_Notification_Date',
          'final_notification_date'
        ]);

        if (!dateStringVal && this.selectedLayoutId) {
          const selectedLayout = this.layouts.find(x => this.getValue(x, ['layoutId', 'LayoutId', 'id', 'Id']) == this.selectedLayoutId);
          if (selectedLayout) {
            dateStringVal = this.getValue(selectedLayout, [
              'FinalNotificationDate',
              'finalNotificationDate',
              'FinalNotificationdate',
              'Final_Notification_Date',
              'final_notification_date'
            ]);
          }
        }
      }

      if (!dateStringVal) { return false; }

      const itemParts = this.parseDateParts(dateStringVal);
      if (!itemParts) { return false; }
      const itemNum = itemParts.year * 10000 + itemParts.month * 100 + itemParts.day;

      if (this.filterType === 'date') {
        const fromParts = this.parseDateParts(this.filterFromDate || this.filterDate);
        const toParts = this.parseDateParts(this.filterToDate);

        if (!fromParts && !toParts) {
          return true;
        }

        if (fromParts) {
          const fromNum = fromParts.year * 10000 + fromParts.month * 100 + fromParts.day;
          if (itemNum < fromNum) return false;
        }

        if (toParts) {
          const toNum = toParts.year * 10000 + toParts.month * 100 + toParts.day;
          if (itemNum > toNum) return false;
        }

        return true;

      } else if (this.filterType === 'month' && this.filterMonth) {
        const matchMonth = itemParts.month === parseInt(this.filterMonth, 10);
        if (!matchMonth) return false;
        if (this.filterYear) {
          return itemParts.year === parseInt(this.filterYear, 10);
        }
        return true;

      } else if (this.filterType === 'year' && this.filterYear) {
        return itemParts.year === parseInt(this.filterYear, 10);
      }

      return true;
    });

    if (this.filteredReportData.length === 0) {
      this.noDataMessage = 'No records found matching the selected filter criteria.';
    } else {
      this.noDataMessage = '';
    }
  }

  onViewerFilterTypeChange(): void {
    this.filterFromDate = '';
    this.filterToDate = '';
    this.filterDate = '';
    this.filterMonth = '';
    this.filterYear = '';
    if (!this.filterField) {
      if (this.selectedReport === this.NOTICE_12_2 || this.selectedReport === this.NOTICE_16_2) {
        this.filterField = 'NoticeDate';
      } else {
        this.filterField = 'FinalNotificationDate';
      }
    }

    if (!this.filterType) {
      this.clearViewerFilter();
    } else {
      this.applyFilters();
    }
  }

  clearViewerFilter(): void {
    this.filterType = '';
    this.filterFromDate = '';
    this.filterToDate = '';
    this.filterDate = '';
    this.filterMonth = '';
    this.filterYear = '';
    if (this.selectedReport === this.NOTICE_12_2 || this.selectedReport === this.NOTICE_16_2) {
      this.filterField = 'NoticeDate';
    } else {
      this.filterField = 'FinalNotificationDate';
    }
    
    if (this.selectedReport && this.individualFilters[this.selectedReport]) {
      this.clearIndividualFilter(this.selectedReport);
    }
    this.applyFilters();
  }

  changeSelection(): void {
    this.selectedLayoutId = 0;
    this.selectedVillageId = 0;
    this.villages = [];
    this.backToReports();
  }

  getArrayFromResponse(data: any): any[] {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.data)) return data.data;
    if (data && Array.isArray(data.result)) return data.result;
    if (data && Array.isArray(data.items)) return data.items;
    return [];
  }

  getValue(item: any, names: string[]): any {
    if (!item) return '';
    for (let i = 0; i < names.length; i++) {
      if (item[names[i]] !== undefined && item[names[i]] !== null) {
        return item[names[i]];
      }
    }
    return '';
  }

  getLayoutName(): string {
    const layout = this.layouts.find(x => this.getValue(x, ['layoutId', 'LayoutId', 'id', 'Id']) == this.selectedLayoutId);
    return this.getValue(layout, ['layoutName', 'LayoutName', 'name', 'Name']);
  }

  getVillageName(): string {
    const village = this.villages.find(x => this.getValue(x, ['villageId', 'VillageId', 'id', 'Id']) == this.selectedVillageId);
    return this.getValue(village, ['villageName', 'VillageName', 'name', 'Name']);
  }

  backToReports(): void {
    this.showReport = false;
    this.selectedReport = '';
    this.reportTitle = '';
    this.reportDescription = '';
    this.reportData = [];
    this.errorMessage = '';
    this.noDataMessage = '';
  }

  printReport(): void {
    const reportElement = document.getElementById('printableReport');
    if (!reportElement) return;

    const printWindow = window.open('', '_blank', 'width=1200,height=800');
    if (!printWindow) {
        alert('Please allow pop-ups to print the report.');
        return;
    }

    printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
            <meta charset="UTF-8">
            <title>${this.reportTitle || 'MIS Report'}</title>
            <style>
                * { box-sizing: border-box; }
                html, body { margin: 0; padding: 0; width: 100%; background: #ffffff; font-family: "Segoe UI", Arial, Helvetica, sans-serif; }
                #printableReport { width: 100%; margin: 0; padding: 0; background: #ffffff; }
                .report-paper { width: 100%; margin: 0; padding: 8mm; background: #ffffff; border: none; box-shadow: none; border-radius: 0; }
                .report-viewer-header, .selection-strip, .report-actions, .back-button, .report-loading, .report-error, .no-report-data, button { display: none !important; }
                .report-header { width: 100%; text-align: center; margin: 0 0 12px 0; padding: 0 0 10px 0; border-bottom: 2px solid #315f72; }
                .government-title { text-align: center; color: #294c5c; font-size: 13px; font-weight: 700; letter-spacing: 0.8px; }
                .report-header h1 { margin: 6px 0 8px 0; color: #173b4d; font-size: 20px; font-weight: 600; text-align: center; }
                .main-report-title { width: 100%; margin: 8px 0 12px 0; padding: 9px 12px; text-align: center; background: #488b93; color: #ffffff; border-radius: 5px; font-size: 16px; font-weight: 600; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                .report-meta { width: 100%; display: flex; justify-content: center; align-items: center; flex-wrap: wrap; gap: 5px 18px; margin: 0 0 10px 0; padding: 0; text-align: center; color: #555555; font-size: 10px; }
                .report-meta span { display: inline-block; }
                .report-meta b { color: #294c5c; font-weight: 600; }
                .report-table, table { width: 100%; border-collapse: collapse; border-spacing: 0; margin-top: 8px; }
                .report-table th, .report-table td, th, td { border: 1px solid #999999; padding: 6px 7px; font-size: 10px; vertical-align: middle; }
                .report-table th, th { background: #edf3f6; color: #294c5c; font-weight: 600; text-align: left; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                .report-table td, td { background: #ffffff; color: #465b64; }
                .report-table th:first-child, .report-table td:first-child, th:first-child, td:first-child { text-align: center; }
                thead { display: table-header-group; }
                tbody { display: table-row-group; }
                tr { page-break-inside: avoid; break-inside: avoid; }
                .notice-report-heading { width: 100%; margin: 8px 0 12px 0; padding: 9px 12px; text-align: center; background: #488b93; color: #ffffff; border-radius: 5px; font-size: 15px; font-weight: 600; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                .notice-section-title { margin: 15px 0 8px 0; padding: 7px 10px; background: #f1f5f7; border-left: 3px solid #315f72; color: #315f72; font-size: 12px; font-weight: 600; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                @page { size: A4 landscape; margin: 10mm; }
            </style>
        </head>
        <body>
            ${reportElement.outerHTML}
            <script>
                window.onload = function () {
                    setTimeout(function () {
                        window.focus();
                        window.print();
                    }, 500);
                };
                window.onafterprint = function () {
                    setTimeout(function () { window.close(); }, 300);
                };
            </script>
        </body>
        </html>
    `);
    printWindow.document.close();
  }

  downloadPDF(): void {
    const reportElement = document.getElementById('printableReport');
    if (!reportElement) return;

    const printWindow = window.open('', '_blank', 'width=1200,height=900');
    if (!printWindow) {
        alert('Please allow pop-ups for this website.');
        return;
    }

    const reportHTML = reportElement.outerHTML;
    printWindow.document.open();
    printWindow.document.write(`
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>${this.reportTitle || 'MIS Report'}</title>
    <style>
        *, *::before, *::after { box-sizing: border-box; }
        html, body { margin: 0 !important; padding: 0 !important; width: 100% !important; min-height: 100% !important; background: #ffffff !important; font-family: "Segoe UI", Arial, Helvetica, sans-serif; color: #333333; }
        #printableReport { display: block !important; width: 100% !important; max-width: none !important; margin: 0 !important; padding: 0 !important; background: #ffffff !important; border: none !important; box-shadow: none !important; }
        .report-paper { display: block !important; width: 100% !important; max-width: none !important; margin: 0 !important; padding: 5mm !important; background: #ffffff !important; border: none !important; border-radius: 0 !important; box-shadow: none !important; }
        .report-viewer-header, .selection-strip, .report-actions, .back-button, .report-loading, .report-error, .no-report-data, button, .btn, .print-button, .download-button { display: none !important; }
        .report-header { display: block !important; width: 100% !important; margin: 0 0 10px 0 !important; padding: 0 0 10px 0 !important; text-align: center !important; border-bottom: 2px solid #315f72 !important; }
        .government-title { display: block !important; margin: 0 !important; padding: 0 !important; text-align: center !important; color: #294c5c !important; font-size: 15px !important; font-weight: 700 !important; letter-spacing: 0.8px !important; }
        .report-header h1 { display: block !important; margin: 4px 0 0 0 !important; padding: 0 !important; color: #294c5c !important; font-size: 12px !important; font-weight: 600 !important; text-align: center !important; }
        .main-report-title { display: block !important; width: 100% !important; margin: 8px 0 12px 0 !important; padding: 9px 12px !important; background: #488b93 !important; color: #ffffff !important; text-align: center !important; font-size: 16px !important; font-weight: 600 !important; border-radius: 5px !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        .notice-report-heading { display: block !important; width: 100% !important; margin: 8px 0 12px 0 !important; padding: 9px 12px !important; background: #488b93 !important; color: #ffffff !important; text-align: center !important; font-size: 15px !important; font-weight: 600 !important; border-radius: 5px !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        .report-meta { display: flex !important; justify-content: center !important; align-items: center !important; flex-wrap: wrap !important; width: 100% !important; margin: 0 0 10px 0 !important; padding: 0 !important; text-align: center !important; color: #555555 !important; font-size: 10px !important; }
        .report-meta-row { display: flex !important; justify-content: center !important; align-items: center !important; flex-wrap: wrap !important; width: 100% !important; gap: 5px 18px !important; margin: 0 0 5px 0 !important; padding: 0 !important; }
        .report-meta span { display: inline-block !important; margin-right: 12px !important; }
        .report-meta b { color: #294c5c !important; font-weight: 600 !important; }
        table { width: 100% !important; border-collapse: collapse !important; margin: 8px 0 0 0 !important; padding: 0 !important; }
        .report-table th { padding: 6px 7px !important; background: #edf3f6 !important; color: #294c5c !important; border: 1px solid #999999 !important; font-size: 10px !important; font-weight: 600 !important; text-align: left !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        .report-table td { padding: 6px 7px !important; background: #ffffff !important; color: #465b64 !important; border: 1px solid #999999 !important; font-size: 10px !important; }
        .report-table th:first-child, .report-table td:first-child { text-align: center !important; }
        .notice-section-title { margin: 12px 0 8px 0 !important; padding: 7px 10px !important; background: #f1f5f7 !important; border-left: 3px solid #315f72 !important; color: #315f72 !important; font-size: 12px !important; font-weight: 600 !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        @page { size: A4 landscape; margin: 10mm; }
    </style>
</head>
<body>
    ${reportHTML}
    <script>
        window.onload = function () {
            setTimeout(function () { window.focus(); window.print(); }, 500);
        };
        window.onafterprint = function () {
            setTimeout(function () { window.close(); }, 500);
        };
    </script>
</body>
</html>
    `);
    printWindow.document.close();
  }

  // exportExcel(): void {
  //   if (!this.filteredReportData || this.filteredReportData.length === 0) return;

  //   const getValue = (item: any, possibleNames: string[]): any => {
  //       for (const name of possibleNames) {
  //           if (item && item[name] !== undefined && item[name] !== null && item[name] !== '') {
  //               return item[name];
  //           }
  //       }
  //       return '';
  //   };

  //   const firstRecord = this.filteredReportData[0];
  //   let title = this.reportTitle || 'MIS Report';
  //   if (this.selectedReport === this.LAND_ACQUISITION) {
  //       title = 'Layout Details Report';
  //   }

  //   const layoutName = getValue(firstRecord, ['layoutName', 'LayoutName', 'Layout', 'layout']) || this.getLayoutName();
  //   const villageName = getValue(firstRecord, ['villageName', 'VillageName', 'Village', 'village']) || this.getVillageName();
  //   const hobli = getValue(firstRecord, ['Hobli', 'hobli']);
  //   const taluk = getValue(firstRecord, ['Taluk', 'taluk']);
  //   const primaryNotification = getValue(firstRecord, ['PrimaryNotificationNo', 'preliminaryNotificationNo', 'PreliminaryNotificationNo']);
  //   const primaryNotificationDate = getValue(firstRecord, ['PrimaryNotificationDate', 'preliminaryNotificationDate', 'PreliminaryNotificationDate']);
  //   const finalNotification = getValue(firstRecord, ['FinalNotificationNo', 'finalNotificationNo']);
  //   const finalNotificationDate = getValue(firstRecord, ['FinalNotificationDate', 'finalNotificationDate']);

  //   const formatDate = (value: any): string => {
  //       if (!value) return '';
  //       const date = new Date(value);
  //       if (isNaN(date.getTime())) return value;
  //       const day = String(date.getDate()).padStart(2, '0');
  //       const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  //       return `${day}.${monthNames[date.getMonth()]}.${date.getFullYear()}`;
  //   };

  //   const now = new Date();
  //   const generatedDate = `${String(now.getDate()).padStart(2, '0')} ${now.toLocaleString('en-US', { month: 'short' })} ${now.getFullYear()}, ${now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;

  //   const rows: any[][] = [];
  //   rows.push(['BANGALORE DEVELOPMENT AUTHORITY']);
  //   rows.push(['GOVERNMENT OF KARNATAKA']);
  //   rows.push([]);
  //   rows.push([title]);

  //   let tableHeader: string[] = ['Sl. No.'];

  //   if (this.selectedReport === this.LAND_ACQUISITION) {
  //       rows.push([`Total Records: ${this.filteredReportData.length}`, `Generated: ${generatedDate}`]);
  //       if (this.filterType) {
  //           let fieldName = 'Final Notification';
  //           if (this.filterField === 'PreliminaryNotificationDate') fieldName = 'Preliminary Notification';
  //           if (this.filterField === 'NoticeDate' || this.selectedReport === this.NOTICE_12_2 || this.selectedReport === this.NOTICE_16_2) fieldName = 'Notice Date';
            
  //           let filterDesc = `Active Filter: ${fieldName} By ${this.filterType}`;
  //           if (this.filterFromDate && this.filterToDate) {
  //               filterDesc += ` - From: ${this.filterFromDate} To: ${this.filterToDate}`;
  //           } else if (this.filterFromDate) {
  //               filterDesc += ` - From: ${this.filterFromDate}`;
  //           } else if (this.filterToDate) {
  //               filterDesc += ` - To: ${this.filterToDate}`;
  //           } else if (this.filterDate) {
  //               filterDesc += ` - Date: ${this.filterDate}`;
  //           }
  //           if (this.filterMonth) filterDesc += ` - Month: ${this.filterMonth}`;
  //           if (this.filterYear) filterDesc += ` - Year: ${this.filterYear}`;
  //           rows.push([filterDesc]);
  //       }
  //       rows.push([]);

  //       tableHeader = ['Sl. No.', 'Layout Name', 'Preliminary Notification No.', 'Preliminary Notification Date', 'Final Notification No.', 'Final Notification Date'];
  //       rows.push(tableHeader);

  //       this.filteredReportData.forEach((item: any, index: number) => {
  //           rows.push([
  //               index + 1,
  //               getValue(item, ['layoutName', 'LayoutName', 'name', 'Name']),
  //               getValue(item, ['PreliminaryNotificationNo', 'preliminaryNotificationNo', 'PreliminaryNotificationNumber', 'preliminaryNotificationNumber']),
  //               getValue(item, ['PreliminaryNotificationDate', 'preliminaryNotificationDate']),
  //               getValue(item, ['FinalNotificationNo', 'finalNotificationNo', 'FinalNotificationNumber', 'finalNotificationNumber']),
  //               getValue(item, ['FinalNotificationDate', 'finalNotificationDate'])
  //           ]);
  //       });
  //   } else {
  //       rows.push([`Layout: ${layoutName}`, `Village: ${villageName}`, `Hobli: ${hobli}`, `Taluk: ${taluk}`, `Generated: ${generatedDate}`]);
  //       rows.push([`Primary Notification: ${primaryNotification}`, `Date: ${formatDate(primaryNotificationDate)}`, `Final Notification: ${finalNotification}`, `Date: ${formatDate(finalNotificationDate)}`]);
  //       rows.push([`Total Records: ${this.filteredReportData.length}`]);
        
  //       if (this.filterType) {
  //           let fieldName = 'Final Notification';
  //           if (this.filterField === 'PreliminaryNotificationDate') fieldName = 'Preliminary Notification';
  //           if (this.filterField === 'NoticeDate' || this.selectedReport === this.NOTICE_12_2 || this.selectedReport === this.NOTICE_16_2) fieldName = 'Notice Date';
            
  //           let filterDesc = `Active Filter: ${fieldName} By ${this.filterType}`;
  //           if (this.filterFromDate && this.filterToDate) {
  //               filterDesc += ` - From: ${this.filterFromDate} To: ${this.filterToDate}`;
  //           } else if (this.filterFromDate) {
  //               filterDesc += ` - From: ${this.filterFromDate}`;
  //           } else if (this.filterToDate) {
  //               filterDesc += ` - To: ${this.filterToDate}`;
  //           } else if (this.filterDate) {
  //               filterDesc += ` - Date: ${this.filterDate}`;
  //           }
  //           if (this.filterMonth) filterDesc += ` - Month: ${this.filterMonth}`;
  //           if (this.filterYear) filterDesc += ` - Year: ${this.filterYear}`;
  //           rows.push([filterDesc]);
  //       }
  //       rows.push([]);

  //       let columns: string[] = Object.keys(firstRecord);
  //       const excludedFields = ['id', 'Id', 'layoutId', 'LayoutId', 'villageId', 'VillageId', 'createdBy', 'CreatedBy', 'createdDate', 'CreatedDate', 'modifiedBy', 'ModifiedBy', 'modifiedDate', 'ModifiedDate'];
  //       columns = columns.filter(x => !excludedFields.includes(x));

  //       const headerName = (key: string): string => {
  //           const map: { [key: string]: string } = {
  //               'SlNo': 'Sl. No.', 'KhatidarName': 'Khatidar Name', 'KhathedarName': 'Khathedar Name',
  //               'Khathedars': 'Khathedars', 'SyNo': 'Sy No.', 'SurveyNo': 'Survey No.', 'Hissa': 'Hissa',
  //               'RTCExtent': 'RTC Extent', 'NotificationExtent': 'Notification Extent', 'KharabExtent': 'Kharab Extent',
  //               'AwardPassedExtent': 'Award Passed Extent', 'Notice12_2Date': '12(2) Notice Date',
  //               'Notice16_2Date': '16(2) Notice Date', 'TahsildarPayment': 'Tahsildar Payment',
  //               'KharabChequeNo': 'Kharab Cheque No.', 'KhatedarCashAmount': 'Khatedar Cash Amount'
  //           };
  //           if (map[key]) return map[key];
  //           return key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
  //       };

  //       tableHeader = ['Sl. No.'];
  //       columns.forEach(column => tableHeader.push(headerName(column)));
  //       rows.push(tableHeader);

  //       this.filteredReportData.forEach((item: any, index: number) => {
  //           const row: any[] = [];
  //           row.push(index + 1);
  //           columns.forEach(column => {
  //               let value = item[column];
  //               if (value === null || value === undefined) value = '';
  //               if (column.toLowerCase().includes('date')) value = formatDate(value);
  //               if (typeof value === 'object' && value !== null) {
  //                   try { value = JSON.stringify(value); } catch { value = ''; }
  //               }
  //               row.push(value);
  //           });
  //           rows.push(row);
  //       });
  //   }

  //   const worksheet = XLSX.utils.aoa_to_sheet(rows);
  //   const totalColumns = tableHeader.length;

  //   worksheet['!merges'] = [
  //       { s: { r: 0, c: 0 }, e: { r: 0, c: totalColumns - 1 } },
  //       { s: { r: 1, c: 0 }, e: { r: 1, c: totalColumns - 1 } },
  //       { s: { r: 3, c: 0 }, e: { r: 3, c: totalColumns - 1 } },
  //       { s: { r: 6, c: 0 }, e: { r: 6, c: totalColumns - 1 } }
  //   ];

  //   const workbook = XLSX.utils.book_new();
  //   XLSX.utils.book_append_sheet(workbook, worksheet, 'MIS Report');

  //   const fileName = title.replace(/[\/\\:*?"<>|]/g, '_') + '.xlsx';
  //   XLSX.writeFile(workbook, fileName);
  // }




  exportExcel(): void {
    if (!this.filteredReportData || this.filteredReportData.length === 0) return;

    const getValue = (item: any, possibleNames: string[]): any => {
        for (const name of possibleNames) {
            if (item && item[name] !== undefined && item[name] !== null && item[name] !== '') {
                return item[name];
            }
        }
        return '';
    };

    const firstRecord = this.filteredReportData[0];
    let title = this.reportTitle || 'MIS Report';
    if (this.selectedReport === this.LAND_ACQUISITION) {
        title = 'Layout Details Report';
    }

    const layoutName = getValue(firstRecord, ['layoutName', 'LayoutName', 'Layout', 'layout']) || this.getLayoutName();
    const villageName = getValue(firstRecord, ['villageName', 'VillageName', 'Village', 'village']) || this.getVillageName();
    const hobli = getValue(firstRecord, ['Hobli', 'hobli']);
    const taluk = getValue(firstRecord, ['Taluk', 'taluk']);
    const primaryNotification = getValue(firstRecord, ['PrimaryNotificationNo', 'preliminaryNotificationNo', 'PreliminaryNotificationNo']);
    const primaryNotificationDate = getValue(firstRecord, ['PrimaryNotificationDate', 'preliminaryNotificationDate', 'PreliminaryNotificationDate']);
    const finalNotification = getValue(firstRecord, ['FinalNotificationNo', 'finalNotificationNo']);
    const finalNotificationDate = getValue(firstRecord, ['FinalNotificationDate', 'finalNotificationDate']);

    const formatDate = (value: any): string => {
        if (!value) return '';
        const date = new Date(value);
        if (isNaN(date.getTime())) return value;
        const day = String(date.getDate()).padStart(2, '0');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${day}.${monthNames[date.getMonth()]}.${date.getFullYear()}`;
    };

    const now = new Date();
    const generatedDate = `${String(now.getDate()).padStart(2, '0')} ${now.toLocaleString('en-US', { month: 'short' })} ${now.getFullYear()}, ${now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;

    const rows: any[][] = [];
    rows.push(['BANGALORE DEVELOPMENT AUTHORITY']);
    rows.push(['GOVERNMENT OF KARNATAKA']);
    rows.push([]);
    rows.push([title]);

    let tableHeader: string[] = ['Sl. No.'];

    if (this.selectedReport === this.LAND_ACQUISITION) {
        rows.push([`Total Records: ${this.filteredReportData.length}`, `Generated: ${generatedDate}`]);
        if (this.filterType) {
            let fieldName = 'Final Notification';
            if (this.filterField === 'PreliminaryNotificationDate') fieldName = 'Preliminary Notification';
            // Removed the NoticeDate check here since it's impossible in this block
            
            let filterDesc = `Active Filter: ${fieldName} By ${this.filterType}`;
            if (this.filterFromDate && this.filterToDate) {
                filterDesc += ` - From: ${this.filterFromDate} To: ${this.filterToDate}`;
            } else if (this.filterFromDate) {
                filterDesc += ` - From: ${this.filterFromDate}`;
            } else if (this.filterToDate) {
                filterDesc += ` - To: ${this.filterToDate}`;
            } else if (this.filterDate) {
                filterDesc += ` - Date: ${this.filterDate}`;
            }
            if (this.filterMonth) filterDesc += ` - Month: ${this.filterMonth}`;
            if (this.filterYear) filterDesc += ` - Year: ${this.filterYear}`;
            rows.push([filterDesc]);
        }
        rows.push([]);

        tableHeader = ['Sl. No.', 'Layout Name', 'Preliminary Notification No.', 'Preliminary Notification Date', 'Final Notification No.', 'Final Notification Date'];
        rows.push(tableHeader);

        this.filteredReportData.forEach((item: any, index: number) => {
            rows.push([
                index + 1,
                getValue(item, ['layoutName', 'LayoutName', 'name', 'Name']),
                getValue(item, ['PreliminaryNotificationNo', 'preliminaryNotificationNo', 'PreliminaryNotificationNumber', 'preliminaryNotificationNumber']),
                getValue(item, ['PreliminaryNotificationDate', 'preliminaryNotificationDate']),
                getValue(item, ['FinalNotificationNo', 'finalNotificationNo', 'FinalNotificationNumber', 'finalNotificationNumber']),
                getValue(item, ['FinalNotificationDate', 'finalNotificationDate'])
            ]);
        });
    } else {
        rows.push([`Layout: ${layoutName}`, `Village: ${villageName}`, `Hobli: ${hobli}`, `Taluk: ${taluk}`, `Generated: ${generatedDate}`]);
        rows.push([`Primary Notification: ${primaryNotification}`, `Date: ${formatDate(primaryNotificationDate)}`, `Final Notification: ${finalNotification}`, `Date: ${formatDate(finalNotificationDate)}`]);
        rows.push([`Total Records: ${this.filteredReportData.length}`]);
        
        if (this.filterType) {
            let fieldName = 'Final Notification';
            if (this.filterField === 'PreliminaryNotificationDate') fieldName = 'Preliminary Notification';
            // It is safe to check for Notice types here in the 'else' block
            if (this.filterField === 'NoticeDate' || this.selectedReport === this.NOTICE_12_2 || this.selectedReport === this.NOTICE_16_2) fieldName = 'Notice Date';
            
            let filterDesc = `Active Filter: ${fieldName} By ${this.filterType}`;
            if (this.filterFromDate && this.filterToDate) {
                filterDesc += ` - From: ${this.filterFromDate} To: ${this.filterToDate}`;
            } else if (this.filterFromDate) {
                filterDesc += ` - From: ${this.filterFromDate}`;
            } else if (this.filterToDate) {
                filterDesc += ` - To: ${this.filterToDate}`;
            } else if (this.filterDate) {
                filterDesc += ` - Date: ${this.filterDate}`;
            }
            if (this.filterMonth) filterDesc += ` - Month: ${this.filterMonth}`;
            if (this.filterYear) filterDesc += ` - Year: ${this.filterYear}`;
            rows.push([filterDesc]);
        }
        rows.push([]);

        let columns: string[] = Object.keys(firstRecord);
        const excludedFields = ['id', 'Id', 'layoutId', 'LayoutId', 'villageId', 'VillageId', 'createdBy', 'CreatedBy', 'createdDate', 'CreatedDate', 'modifiedBy', 'ModifiedBy', 'modifiedDate', 'ModifiedDate'];
        columns = columns.filter(x => !excludedFields.includes(x));

        const headerName = (key: string): string => {
            const map: { [key: string]: string } = {
                'SlNo': 'Sl. No.', 'KhatidarName': 'Khatidar Name', 'KhathedarName': 'Khathedar Name',
                'Khathedars': 'Khathedars', 'SyNo': 'Sy No.', 'SurveyNo': 'Survey No.', 'Hissa': 'Hissa',
                'RTCExtent': 'RTC Extent', 'NotificationExtent': 'Notification Extent', 'KharabExtent': 'Kharab Extent',
                'AwardPassedExtent': 'Award Passed Extent', 'Notice12_2Date': '12(2) Notice Date',
                'Notice16_2Date': '16(2) Notice Date', 'TahsildarPayment': 'Tahsildar Payment',
                'KharabChequeNo': 'Kharab Cheque No.', 'KhatedarCashAmount': 'Khatedar Cash Amount'
            };
            if (map[key]) return map[key];
            return key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
        };

        tableHeader = ['Sl. No.'];
        columns.forEach(column => tableHeader.push(headerName(column)));
        rows.push(tableHeader);

        this.filteredReportData.forEach((item: any, index: number) => {
            const row: any[] = [];
            row.push(index + 1);
            columns.forEach(column => {
                let value = item[column];
                if (value === null || value === undefined) value = '';
                if (column.toLowerCase().includes('date')) value = formatDate(value);
                if (typeof value === 'object' && value !== null) {
                    try { value = JSON.stringify(value); } catch { value = ''; }
                }
                row.push(value);
            });
            rows.push(row);
        });
    }

    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    const totalColumns = tableHeader.length;

    worksheet['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: totalColumns - 1 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: totalColumns - 1 } },
        { s: { r: 3, c: 0 }, e: { r: 3, c: totalColumns - 1 } },
        { s: { r: 6, c: 0 }, e: { r: 6, c: totalColumns - 1 } }
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'MIS Report');

    const fileName = title.replace(/[\/\\:*?"<>|]/g, '_') + '.xlsx';
    XLSX.writeFile(workbook, fileName);
  }













}


// WEBPACK FOOTER //
// ./src/app/reports/reports.component.ts